<template>
  <div>
    <div class="border-bottom d-flex">
      <div>스토리 등록하기</div>
      <how-to-guide
          class="ml-2 d-flex align-center"
          img-src="https://firebasestorage.googleapis.com/v0/b/onu-business-profile-1b72d.appspot.com/o/business-profile%2Fbusiness-profile-5.png?alt=media&token=7452594c-e818-447e-a20a-bc9eca2a6e16"/>
    </div>
    <div class="border-bottom-text">매장을 매력적으로 어필할 수 있는 이야기를 고객들에게 들려주세요.</div>
    <!-- checkbox should stay outside of v-form in order to set v-checkbox's value to true when registering or updating -->
    <v-checkbox
        v-model="storyForm.isActive"
        class="mt-0"
        color="primary"
        label="스토리 전체 공개"
        :messages="'스토리를 전체공개 또는 비공개로 설정할 수 있습니다.'"
    />
    <v-row>
      <v-col>
        <v-form
            ref="storyForm"
            v-model="valid"
            lazy-validation
        >
          <v-text-field
              ref="firstInput"
              v-model="storyForm.title"
              label="제목 *"
              placeholder="ex) 오늘도 바쁜 하루를 보낸 당신"
              :rules="titleRules"
              required
          />
          <v-textarea
              v-model="storyForm.description"
              class="mt-3"
              outlined
              label="본문 *"
              placeholder="(ex) 오늘 하루도 바쁘게 보내셨나요? 할 일은 너무 많은데 우리에게 주어진 시간은 너무 짧습니다. 그래도, 지금 이 시간만큼은 이 곳에서 잠시 멈추고 숨을 돌리는 건 어떠세요? For Bar는 지친 감정을 감싸줄 수 있는 다양한 칵테일 메뉴를 선사합니다. 지난 20년 동안 많은 분들이 방문했고, For Bar에는 수많은 사람들의 이야기가 남아있습니다. 오늘 하루, 당신의 감정을 다른 사람의 이야기 사이로 조심스럽게 내놓아보세요."
              :rules="descriptionRules"
              required
          />
        </v-form>
      </v-col>
      <v-col cols="12" sm="4">
        <image-upload
            label="스토리 사진"
            :img-url="storyForm.imgUrl"
            :full-path="storyForm.fullPath"
            @imgUploaded="imgUploaded"
            ref="imageUploadComponent"
        />
      </v-col>
    </v-row>

    <div class="d-flex justify-end align-center mt-5">
      <v-btn
          color="onuGreyColor"
          outlined
          class="onu-button-style"
          @click="resetForm">
        취소
      </v-btn>
      <v-btn
          v-if="storyForm.id"
          class="onu-button-style primary"
          @click="openAndCloseProgressCircularWithDebounce(registerStory, 100, true)">
        수정
      </v-btn>
      <v-btn
          v-else
          class="onu-button-style primary"
          @click="openAndCloseProgressCircularWithDebounce(registerStory, 100)">
        등록
      </v-btn>
    </div>
  </div>
</template>

<script>
import ImageUpload from "../Common/ImageUpload";
import {collectionNames, simpleCreate, simpleReadById, simpleUpdate} from "@/firebase/firestore";
import behavior from "../../constants/behavior";
import {EventBus} from "@/plugins/event-bus";
import {deleteImg, storageNames, uploadImgAndReturnFileMetadata} from "@/firebase/firestorage";
import utils from "../../mixins/utils";
import HowToGuide from "../Common/HowToGuide";

export default {
  name: "StoryForm",
  components: {
    'image-upload': ImageUpload,
    'how-to-guide': HowToGuide
  },
  mounted() {
    EventBus.$on(behavior.UPDATE_STORY, story => {
      this.storyForm = {...this.storyForm, ...story}
      this.focusFirstInput()
    })
  },
  data: () => ({
    valid: true,
    storyForm: {
      title: '',
      description: '',
      isActive: true,
      imgUrl: null,
      fullPath: null,
    },
    imgFile: null,
    titleRules: [
      v => !!v || '필수 입력사항입니다.',
    ],
    descriptionRules: [
      v => !!v || '필수 입력사항입니다.',
    ]
  }),
  mixins: [utils],
  methods: {
    async registerStory(isUpdate = false) {
      if (!this.$refs.storyForm.validate()) {
        EventBus.$emit(behavior.OPEN_SNACK_BAR_ERROR, '입력 정보를 확인해주세요.')
        return
      }

      // store storyForm.description in a json format
      this.storyForm.description = JSON.stringify(this.storyForm.description)

      // if updating story, story id is required
      if (isUpdate) {
        if (!this.storyForm.id) {
          EventBus.$emit(behavior.OPEN_SNACK_BAR_ERROR, '스토리를 우선 등록해주세요.')
        } else {
          await this.updateStory()
        }
        return
      }

      // for non-updating operations, story id isn't required
      if (this.storyForm.id) {
        EventBus.$emit(behavior.OPEN_SNACK_BAR_ERROR, '수정 버튼을 눌러주세요.')
        return
      }

      try {
        if (this.imgFile) {
          const imgMetadata = await uploadImgAndReturnFileMetadata({
            storageName: storageNames.storyImg,
            file: this.imgFile,
          })
          this.storyForm.fullPath = imgMetadata.metadata.fullPath
          this.storyForm.imgUrl = imgMetadata.url
        }

        const docRef = await simpleCreate(collectionNames.story, {
          ...this.storyForm,
          storeId: this.$store.getters.getStoreId
        })

        const doc = await simpleReadById(collectionNames.story, docRef.id)
        this.$store.commit(behavior.ADD_STORY, {id: docRef.id, ...doc})
        EventBus.$emit(behavior.OPEN_SNACK_BAR_PRIMARY, '스토리를 등록했습니다.')
        this.resetForm()
      } catch (e) {
        EventBus.$emit(behavior.OPEN_SNACK_BAR_ERROR, '스토리 등록에 실패했습니다.')
      }
    },
    async updateStory() {
      try {
        if (this.imgFile) {
          if (this.storyForm.fullPath) deleteImg(this.storyForm.fullPath)
          const imgMetadata = await uploadImgAndReturnFileMetadata({
            storageName: storageNames.storyImg,
            file: this.imgFile,
          })
          this.storyForm.fullPath = imgMetadata.metadata.fullPath
          this.storyForm.imgUrl = imgMetadata.url
        }

        if (!this.imgFile && !this.storyForm.imgUrl) {
          if (this.storyForm.fullPath) await deleteImg(this.storyForm.fullPath)
          this.storyForm.imgUrl = null
          this.storyForm.fullPath = null
        }

        await simpleUpdate(collectionNames.story, this.storyForm, this.storyForm.id)
        this.$store.commit(behavior.UPDATE_STORY, this.storyForm)
        EventBus.$emit(behavior.OPEN_SNACK_BAR_PRIMARY, '스토리를 성공적으로 수정했습니다.')
        this.resetForm()
      } catch (e) {
        EventBus.$emit(behavior.OPEN_SNACK_BAR_ERROR, '스토리를 수정하는 과정에서 오류가 발생했습니다.')
      }
    },
    imgUploaded({file, tempImgUrl}) {
      this.imgFile = file
      this.storyForm.imgUrl = tempImgUrl
    },
    resetForm() {
      this.$refs.storyForm.reset()
      this.$refs.imageUploadComponent.resetFile()
      this.storyForm = {
        title: '',
        description: '',
        isActive: true,
        imgUrl: '',
        fullPath: '',
      }
      this.imgFile = null
      EventBus.$emit(behavior.CLOSE_ACTIVE_EXPANSION_PANELS)
    },
    focusFirstInput() {
      if (this.$refs.firstInput) this.$refs.firstInput.focus()
    }
  }
}
</script>

<style scoped>

</style>